import React from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import {
  Box,
  Button,
  RadioGroupForm,
  ErrorList,
  Spinner,
  Heading,
} from "@ogt/ui";
import { useNavigate } from "react-router-dom";
import { URLS } from "src/config/urls";

const YES = "true";
const NO = "false";

declare global {
  interface Window {
    __newsletterRedirectDone?: boolean;
  }
}

type NewsletterModel = {
  choice?: string;
};

const apiBaseUrl = process.env.REACT_APP_API_BASE_URL?.replace(/\/$/, "") ?? "";

const STAR_COOKIE_NAME = "StarJwtTokenCookieName";
const TOKEN_HEADER_NAME = "X-StMklToken";

// Get cookie-value by name
function getCookieValue(name: string): string | null {
  const cookies = document.cookie
    ? document.cookie.split(";").map((c) => c.trim())
    : [];
  const match = cookies.find((c) =>
    c.startsWith(`${encodeURIComponent(name)}=`)
  );
  if (!match) return null;
  return decodeURIComponent(match.substring(match.indexOf("=") + 1));
}

// Extract Token value from cookie string
function extractToken(cookieRawValue: string): string | null {
  const params = new URLSearchParams(cookieRawValue);
  return params.get("Token");
}

// Create headers with auth token
function authHeaders(token: string): HeadersInit {
  return {
    "Content-Type": "application/json",
    [TOKEN_HEADER_NAME]: token,
  };
}

// Convert subscription boolean to choice string
function subscriptionToChoice(isSubscribed: boolean): string {
  return isSubscribed ? YES : NO;
}

// Convert choice string to subscription boolean
function choiceToSubscription(choice?: string): boolean {
  return choice === YES;
}

// Main component
export default function HandleNewsletter() {
  const [token, setToken] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [fatalError, setFatalError] = React.useState<string | null>(null);
  const [initialChoice, setInitialChoice] = React.useState<string>("");
  const navigate = useNavigate();

  // On component mount, check authentication and fetch current newsletter setting
  React.useEffect(() => {
    const cookieVal = getCookieValue(STAR_COOKIE_NAME);

    if (!cookieVal) {
      setFatalError(
        "Du behöver vara inloggad för att hantera nyhetsbrev. Om du redan loggat in, testa att ladda om sidan."
      );

      if (!window.__newsletterRedirectDone) {
        window.__newsletterRedirectDone = true;
        setTimeout(() => {
          window.open(URLS.loginUrl, "_blank", "noopener,noreferrer");
        }, 1500);
      }

      setLoading(false);
      return;
    }

    // Extract token from cookie
    const extracted = extractToken(cookieVal);
    if (!extracted) {
      setFatalError(
        "Inloggningsinformation saknas eller är ogiltig. Logga in igen."
      );
      setLoading(false);
      return;
    }

    setToken(extracted);

    // Fetch current newsletter subscription status
    (async () => {
      try {
        const res = await fetch(
          `${apiBaseUrl}/api/v1/Users/Current/AcceptNewsletters`,
          {
            method: "GET",
            headers: authHeaders(extracted),
          }
        );

        if (res.status === 401) {
          setFatalError("Du behöver vara inloggad för att hantera nyhetsbrev.");
          setLoading(false);
          return;
        }

        if (!res.ok) {
          throw new Error(`GET AcceptNewsletters misslyckades (${res.status})`);
        }

        const isSubscribed = (await res.json()) as boolean;

        setInitialChoice(subscriptionToChoice(isSubscribed));
      } catch (e) {
        setFatalError("Kunde inte hämta nuvarande inställning för nyhetsbrev.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <>
        <Spinner />
      </>
    );
  }

  if (fatalError) {
    return (
      <>
        <p role="alert">{fatalError}</p>
      </>
    );
  }

  return (
    <>
      <p className="max-w-2xl">
        I nyhetsbreven får du bland annat information om förändringar i
        trafiken, nya funktioner i appen, priser och biljetter, tips inför resan
        och mycket mer.
      </p>

      <Formik<NewsletterModel>
        enableReinitialize
        initialValues={{ choice: initialChoice }}
        validateOnChange={false}
        validateOnBlur={false}
        validationSchema={Yup.object({
          choice: Yup.string()
            .oneOf([YES, NO], "Du måste välja ett alternativ.")
            .required("Du måste välja ett alternativ."),
        })}
        onSubmit={async (values, { setSubmitting }) => {
          try {
            if (!token) throw new Error("Missing token");

            const accept = choiceToSubscription(values.choice);

            const res = await fetch(
              `${apiBaseUrl}/api/v1/Users/Current/AcceptNewsletters/${
                accept ? "true" : "false"
              }`,
              {
                method: "PUT",
                headers: authHeaders(token),
              }
            );

            if (res.status === 401) {
              setFatalError(
                "Du behöver vara inloggad för att hantera nyhetsbrev. Om du redan loggat in, testa att ladda om sidan."
              );
              setTimeout(() => {
                window.open(URLS.loginUrl, "_blank", "noopener,noreferrer");
              }, 1500);

              return;
            }

            if (!res.ok) {
              throw new Error(
                `PUT AcceptNewsletters misslyckades (${res.status})`
              );
            }
            if (loading) {
              <Spinner />;
            } else {
              navigate(
                accept ? "/prenumeration-startad" : "/prenumeration-avslutad"
              );
            }
          } catch {
            // Handle error (e.g. show notification)
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({ errors, isSubmitting }) => (
          <Form noValidate>
            <Box>
              <Heading>Vill du få våra nyhetsbrev?</Heading>

              {Object.keys(errors).length > 0 && <ErrorList errors={errors} />}

              <div className={errors.choice ? "has-error" : ""}>
                <RadioGroupForm
                  name="choice"
                  description="Nyhetsbrev"
                  legend="Var god ange om du vill ta emot nyhetsbrev från oss"
                  orientation="vertical"
                  variant="default"
                  options={[
                    {
                      id: "newsletter-yes",
                      value: YES,
                      label: "Jag vill ha nyhetsbrev",
                    },
                    {
                      id: "newsletter-no",
                      value: NO,
                      label: "Jag vill inte ha nyhetsbrev",
                    },
                  ]}
                />
              </div>
            </Box>

            <div className="flex justify-end">
              {isSubmitting ? (
                <Spinner size={48} />
              ) : (
                <Button type="submit" disabled={isSubmitting}>
                  Spara
                </Button>
              )}
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
}
